import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Paper, TextField, Button, Typography, Box, CircularProgress, IconButton, Chip, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Check as CheckIcon, Close as CloseIcon, Bookmark as BookmarkIcon, Star as StarIcon, MoreVert as MoreVertIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSubscription, SubscriptionTier } from '../contexts/SubscriptionContext';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, serverTimestamp, collection, query, orderBy, addDoc, onSnapshot, getDoc, getDocs, where, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import { auth } from '../config/firebase';
import SubscriptionBanner from '../components/subscription/SubscriptionBanner';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  userId: string;
}

interface Thread {
  id: string;        // Required primary identifier
  threadId: string;  // Required duplicate of id for compatibility
  title?: string;
  createdAt: any;
  lastMessageAt: any;
  isBookmarked?: boolean;
}

export default function Chat() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { tier, dailyMessageCount, incrementMessageCount, limits } = useSubscription();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentThread, setCurrentThread] = useState<Thread | null>(null);
  const [threads, setThreads] = useState<Thread[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchWithRetry = useCallback(async <T,>(
    fn: () => Promise<T>,
    retries = 3,
    delay = 1000
  ): Promise<T> => {
    try {
      return await fn();
    } catch (error: any) {
      if (retries === 0 || error.code === 'functions/unauthenticated') {
        throw error;
      }
      console.log(`Retrying... ${retries} attempts left`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(fn, retries - 1, delay * 1.5);
    }
  }, []);

  const fetchMessages = useCallback(async (threadId: string): Promise<Message[]> => {
    console.log(`fetchMessages called with threadId: "${threadId}" (type: ${typeof threadId})`);
    
    try {
      // Validate input
      if (threadId === undefined || threadId === null) {
        const errorMsg = 'threadId is undefined or null in fetchMessages';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (typeof threadId !== 'string') {
        const errorMsg = `threadId has invalid type: ${typeof threadId} (value: ${String(threadId)})`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      const trimmedThreadId = threadId.trim();
      if (trimmedThreadId === '') {
        const errorMsg = 'threadId is empty string';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      console.log('Fetching messages for thread:', trimmedThreadId);
      
      // Check authentication first
      if (!auth.currentUser) {
        const errorMsg = 'User is not authenticated. Please sign in again.';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      // Initialize the function with explicit region
      const getMessagesFn = httpsCallable<
        { threadId: string },
        { messages: Array<{
          id: string;
          content: string;
          role: 'user' | 'assistant';
          timestamp: number;
          userId: string;
        }> }
      >(functions, 'getMessages');
      
      // Ensure proper payload structure
      const payload = { threadId: trimmedThreadId };
      console.log('Sending payload to getMessages:', JSON.stringify(payload, null, 2));
      
      const result = await fetchWithRetry(async () => {
        try {
          console.log('Calling getMessages Cloud Function...');
          const response = await getMessagesFn(payload);
          console.log('Raw response from getMessages:', JSON.stringify(response.data, null, 2));
          
          // Validate response structure
          if (!response?.data) {
            throw new Error('Server response is missing data');
          }
          
          if (!response.data.messages) {
            throw new Error('Server response is missing messages array');
          }
          
          if (!Array.isArray(response.data.messages)) {
            throw new Error('Server response messages is not an array');
          }
          
          return response;
        } catch (error: any) {
          console.error('Error calling getMessages:', error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          console.error('Error details:', error.details);
          
          if (error.code === 'functions/invalid-argument') {
            console.error('Invalid argument error. Payload was:', JSON.stringify(payload, null, 2));
          }
          throw error;
        }
      });
      
      // Process response
      if (!result?.data?.messages || !Array.isArray(result.data.messages)) {
        console.error('Received invalid response from getMessages:', result);
        throw new Error('Invalid response format from server');
      }
      
      // Convert timestamps to Date objects and validate message structure
      const processedMessages = result.data.messages.map((msg, index) => {
        if (!msg.id || !msg.content || !msg.role || !msg.timestamp || !msg.userId) {
          console.error(`Invalid message at index ${index}:`, msg);
          throw new Error(`Message at index ${index} is missing required fields`);
        }
        
        return {
          ...msg,
          timestamp: new Date(msg.timestamp)
        };
      });
      
      console.log(`Successfully processed ${processedMessages.length} messages`);
      return processedMessages;
    } catch (error: any) {
      console.error('Error in fetchMessages:', error);
      
      // Enhanced error handling with specific messages
      if (error.code === 'functions/not-found') {
        throw new Error('Thread not found. It may have been deleted.');
      }
      if (error.code === 'functions/invalid-argument') {
        throw new Error(`Invalid thread ID format provided: ${threadId}`);
      }
      if (error.code === 'functions/unauthenticated') {
        throw new Error('Your session has expired. Please sign in again.');
      }
      if (error.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to access this thread.');
      }
      
      throw new Error(`Failed to fetch messages: ${error.message || 'Unknown error'}`);
    }
  }, [fetchWithRetry]);

  const fetchThreads = useCallback(async () => {
    if (!currentUser) {
      console.log('No current user, skipping fetchThreads');
      return;
    }
    
    // Add throttling - prevent multiple fetches within 2 seconds
    const now = Date.now();
    if (now - lastFetchTime < 2000) {
      console.log('Throttling fetchThreads call - too many requests');
      return;
    }
    setLastFetchTime(now);
    
    try {
      console.log('Fetching threads...');
      const getThreadsFn = httpsCallable<{}, { threads: Thread[] }>(functions, 'getThreads');
      const result = await getThreadsFn();
      
      // Verify data structure before proceeding
      if (!result?.data || result.data.threads === undefined) {
        console.error('Invalid response format from getThreads:', result);
        setError('Failed to load conversations. Invalid data format.');
        return;
      }
      
      // No need to cast, TypeScript now knows the structure
      const threadsData = result.data;
      
      // Check if threads array is valid
      if (!Array.isArray(threadsData.threads)) {
        console.error('Threads data is not an array:', threadsData);
        setError('Failed to load conversations. Invalid data format.');
        return;
      }
      
      console.log('Fetched threads:', threadsData.threads);
      
      // Normalize thread data to ensure both id and threadId are set
      const normalizedThreads = threadsData.threads.map(thread => {
        const threadId = thread.threadId || thread.id;
        // Skip invalid threads
        if (!threadId || typeof threadId !== 'string' || threadId.trim() === '') {
          console.error('Skipping thread with invalid ID:', thread);
          return null;
        }
        
        return {
          ...thread,
          id: threadId,
          threadId: threadId  // Ensure both are set to the same value
        };
      }).filter(thread => thread !== null) as Thread[];
      
      console.log('Normalized threads:', normalizedThreads);
      setThreads(normalizedThreads);

      // Only select the most recent thread if we don't have a current thread
      // and there's no threadId in the location state
      if (!currentThread && normalizedThreads.length > 0 && !location.state?.threadId) {
        const mostRecentThread = normalizedThreads[0];
        console.log('Selecting most recent thread:', mostRecentThread);
        
        // Double check that the thread has valid ID
        if (mostRecentThread && 
            mostRecentThread.threadId && 
            typeof mostRecentThread.threadId === 'string' && 
            mostRecentThread.threadId.trim() !== '') {
          
          // Set current thread first
          setCurrentThread(mostRecentThread);
          
          try {
            console.log(`Fetching messages for most recent thread: ${mostRecentThread.threadId}`);
            const messages = await fetchWithRetry(() => fetchMessages(mostRecentThread.threadId));
            console.log(`Successfully fetched ${messages.length} messages`);
            setMessages(messages);
          } catch (err) {
            console.error('Error fetching messages for most recent thread:', err);
            setError('Failed to load chat messages. Please try again.');
          }
        } else {
          console.error('Most recent thread has invalid ID:', mostRecentThread);
          setError('Failed to select conversation: Invalid thread data');
        }
      }
    } catch (err) {
      console.error('Error fetching threads:', err);
      setError('Failed to load conversations. Please try again.');
    }
  }, [currentUser, currentThread, location.state?.threadId, fetchWithRetry, fetchMessages]);

  const handleSelectThread = useCallback(async (thread: Thread) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate thread object
      if (!thread) {
        console.error('Thread object is null or undefined');
        setError('Invalid thread data. Please try refreshing the page.');
        return;
      }
      
      // Logging the thread for debugging
      console.log('Thread object being selected:', JSON.stringify(thread, null, 2));
      
      // Make sure we have a valid ID (either id or threadId property)
      const threadId = thread.threadId || thread.id;
      
      if (!threadId || typeof threadId !== 'string' || threadId.trim() === '') {
        console.error('Invalid thread ID:', threadId);
        console.error('Full thread object:', thread);
        setError('Invalid thread ID. Please try a different conversation.');
        return;
      }
      
      console.log(`Selecting thread with ID: ${threadId}`);
      
      // Store the thread in state with consistent ID property
      const normalizedThread: Thread = {
        ...thread,
        id: threadId,
        threadId: threadId  // Ensure both id and threadId are set to the same value
      };
      
      setCurrentThread(normalizedThread);
      
      try {
        console.log(`Fetching messages for thread: ${threadId}`);
        const threadMessages = await fetchWithRetry(() => fetchMessages(threadId));
        console.log(`Fetched ${threadMessages.length} messages for thread: ${threadId}`);
        setMessages(threadMessages);
      } catch (err: any) {
        console.error(`Error fetching messages for thread ${threadId}:`, err);
        setError(`Failed to load messages: ${err.message || 'Please try again.'}`);
      }
    } catch (err: any) {
      console.error('Error in handleSelectThread:', err);
      setError(`An error occurred while selecting the conversation: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  }, [fetchMessages, fetchWithRetry]);

  // Fetch existing threads
  useEffect(() => {
    if (!currentUser) {
      console.log('No current user, skipping fetchThreads');
      return;
    }
    
    // Handle initial thread loading
    const loadInitialData = async () => {
      setLoading(true);
      setError(null); // Clear previous errors
      
      try {
        console.log('Loading initial data...');
        await fetchThreads();
        
        // Additional check: If a threadId was passed via location state, use it
        if (location.state?.threadId) {
          try {
            const stateThreadId = location.state.threadId;
            console.log('Attempting to load thread from location state:', stateThreadId);
            
            if (typeof stateThreadId !== 'string' || stateThreadId.trim() === '') {
              throw new Error(`Invalid threadId in location state: ${String(stateThreadId)}`);
            }
            
            // First, check if this thread exists in our loaded threads
            const threadFromState = threads.find(t => 
              t && ((t.id && t.id === stateThreadId) || (t.threadId && t.threadId === stateThreadId))
            );
            
            if (threadFromState) {
              console.log('Found thread in loaded threads, selecting it:', threadFromState);
              
              // Double-check thread ID validity
              if (!threadFromState.threadId || typeof threadFromState.threadId !== 'string' || threadFromState.threadId.trim() === '') {
                throw new Error('Found thread has invalid threadId');
              }
              
              await handleSelectThread(threadFromState);
            } else {
              // If we can't find it in our list (maybe it was just created), 
              // create a temporary Thread object and try to fetch messages
              console.log('Thread not found in loaded threads, attempting to fetch messages directly');
              
              // Create a normalized temporary thread
              const tempThread: Thread = {
                id: stateThreadId,
                threadId: stateThreadId,  // Explicitly set both to the same value
                createdAt: new Date(),
                lastMessageAt: new Date()
              };
              
              try {
                // Try to get messages to confirm the thread exists
                console.log(`Attempting to fetch messages for thread ID: ${stateThreadId}`);
                const threadMessages = await fetchWithRetry(() => fetchMessages(stateThreadId));
                console.log(`Successfully fetched ${threadMessages.length} messages for thread: ${stateThreadId}`);
                
                // If we get here, the thread exists, so select it
                setCurrentThread(tempThread);
                setMessages(threadMessages);
              } catch (fetchError: any) {
                console.error(`Error fetching messages for thread ${stateThreadId}:`, fetchError);
                setError(`Could not load messages: ${fetchError.message || 'Unknown error'}`);
              }
            }
          } catch (err: any) {
            console.error('Error loading thread from location state:', err);
            setError(`Failed to load the selected conversation: ${err.message || 'Please try again.'}`);
          }
        }
      } catch (err: any) {
        console.error('Error in initial data loading:', err);
        setError(`Failed to load conversations: ${err.message || 'Please refresh the page.'}`);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [
    currentUser, 
    location.state, 
    fetchThreads, 
    fetchMessages, 
    fetchWithRetry, 
    handleSelectThread
  ]);

  const createNewThread = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Calling createThread function...');
      
      // Get current user token for authentication
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        setError('You must be logged in to create a new chat.');
        setLoading(false);
        return;
      }
      
      // Try the callable function first
      try {
        console.log('Attempting to use Firebase callable function...');
        
        // Provide a default title
        const createThreadFn = httpsCallable<{title?: string}, {id: string, title: string, createdAt: number, lastMessageAt: number}>(
          functions, 
          'createThread'
        );
        
        console.log('Function initialized, making the call with title: New Conversation');
        
        const result = await createThreadFn({
          title: 'New Conversation'
        });
        
        console.log('createThread result:', result.data);
  
        // The Cloud Function now returns the complete thread object
        // Make sure we set both id and threadId to the same value
        const threadId = result.data.id;
        
        if (!threadId || typeof threadId !== 'string' || threadId.trim() === '') {
          throw new Error('Invalid thread ID returned from server');
        }
        
        const thread: Thread = {
          id: threadId,
          threadId: threadId, // Explicitly set both to the same value
          title: result.data.title,
          createdAt: new Date(result.data.createdAt),
          lastMessageAt: new Date(result.data.lastMessageAt),
          isBookmarked: false
        };
        
        console.log('Created new thread:', thread);
        
        // No need to store in Firestore again, the function did it
        handleSelectThread(thread);
        
        // Refresh threads to get the latest list
        fetchThreads();
        return; // Exit if the callable function worked
      } catch (fnError: any) {
        console.error('Error calling createThread callable function:', fnError);
        console.log('Falling back to HTTP endpoint...');
        
        // Try the HTTP endpoint as a fallback
        try {
          // Get the project ID from Firebase config
          const projectId = auth.app.options.projectId;
          const apiUrl = `https://us-central1-${projectId}.cloudfunctions.net/createThreadHttp`;
          
          console.log('Making HTTP request to:', apiUrl);
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              title: 'New Conversation'
            })
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error ${response.status}: ${errorText}`);
          }
          
          const responseData = await response.json();
          console.log('HTTP response:', responseData);
          
          if (responseData.success && responseData.thread) {
            const threadId = responseData.thread.id;
            
            if (!threadId || typeof threadId !== 'string' || threadId.trim() === '') {
              throw new Error('Invalid thread ID returned from server');
            }
            
            const thread: Thread = {
              id: threadId,
              threadId: threadId, // Explicitly set both to the same value
              title: responseData.thread.title,
              createdAt: new Date(responseData.thread.createdAt),
              lastMessageAt: new Date(responseData.thread.lastMessageAt),
              isBookmarked: false
            };
            
            console.log('Created new thread via HTTP:', thread);
            handleSelectThread(thread);
            fetchThreads();
          } else {
            throw new Error('Invalid response format');
          }
        } catch (httpError: any) {
          console.error('Error calling HTTP endpoint:', httpError);
          throw httpError; // Re-throw to be caught by the outer catch
        }
      }
    } catch (err: any) {
      console.error('Error creating thread:', err);
      setError(`Failed to create new chat: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Enhanced thread validation
    if (!currentThread) {
      setError("No active conversation selected. Please select or create a conversation first.");
      return;
    }
    
    // Get the correct thread ID
    const threadId = currentThread.threadId || currentThread.id;
    if (!threadId) {
      console.error("Missing thread ID in current thread:", currentThread);
      setError("Invalid conversation data. Please try selecting a different conversation.");
      return;
    }
    
    // Validate message content
    if (!message.trim()) {
      setError("Please enter a message before sending.");
      return;
    }

    // Check subscription limits before sending
    console.log("Current subscription tier:", tier, "Daily count:", dailyMessageCount, "Limit:", limits.dailyMessageLimit);
    
    if (tier === SubscriptionTier.FREE && dailyMessageCount >= limits.dailyMessageLimit) {
      console.log("User has exceeded their daily message limit");
      setQuotaExceeded(true);
      setError(`You've reached your daily limit of ${limits.dailyMessageLimit} messages. Upgrade to Premium for unlimited messages.`);
      return;
    } else {
      console.log("User is allowed to send messages - Tier:", tier, "Count:", dailyMessageCount, "Limit:", limits.dailyMessageLimit);
    }

    const userMessage = message;
    setMessage('');
    setLoading(true);
    setError(null);

    try {
      console.log(`Sending message in thread ${threadId}: ${userMessage}`);
      
      // Add optimistic update - add the message to UI immediately
      const optimisticMessage: Message = {
        id: 'temp-' + Date.now(),
        content: userMessage,
        role: 'user',
        timestamp: new Date(),
        userId: currentUser?.uid || 'unknown'
      };
      
      setMessages(prev => [...prev, optimisticMessage]);
      
      // Get the sendMessage Cloud Function
      const sendMessageFn = httpsCallable<
        { threadId: string, message: string },
        { 
          success: boolean,
          subscriptionInfo?: {
            tier: string;
            dailyMessageCount: number;
            dailyMessageLimit: number;
          }
        }
      >(functions, 'sendMessage');
      
      console.log('Calling sendMessage Cloud Function with threadId:', threadId);
      
      // Call the function with proper parameters
      const result = await sendMessageFn({
        threadId: threadId,
        message: userMessage,
      });
      
      console.log('sendMessage result:', result.data);
      
      // If we got back subscription info, update our local state
      if (result.data.subscriptionInfo) {
        // Update the message count in the UI based on the server response
        // This ensures our UI is in sync with the backend count
        console.log('Updating subscription info from server:', result.data.subscriptionInfo);
      }
      
      // Increment the message count in the context
      incrementMessageCount();
      
      // Success! Let's refresh the messages to get the assistant's response
      console.log('Message sent successfully, fetching updated messages...');
      const updatedMessages = await fetchWithRetry(() => fetchMessages(threadId));
      setMessages(updatedMessages);
    } catch (err: any) {
      console.error('Error sending message:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      console.error('Error details:', err.details);
      
      // Remove the optimistic message since it failed to send
      setMessages(prev => prev.filter(msg => !msg.id.startsWith('temp-')));
      
      // Show specific error based on the error code
      if (err.code === 'functions/unauthenticated') {
        setError('Your session has expired. Please sign in again.');
      } else if (err.code === 'functions/not-found') {
        setError('This conversation no longer exists.');
      } else if (err.code === 'functions/invalid-argument') {
        setError('Invalid message format. Please try again.');
      } else if (err.code === 'functions/unavailable') {
        setError('Service is currently unavailable. Please try again later.');
      } else if (err.code === 'functions/resource-exhausted') {
        setQuotaExceeded(true);
        setError('You have reached your daily message limit. Please upgrade to Premium for unlimited messages.');
      } else {
        setError(`Failed to send message: ${err.message || 'Please try again'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTitleEdit = () => {
    if (currentThread) {
      setNewTitle(currentThread.title || '');
      setEditingTitle(true);
    }
  };

  const handleTitleSave = async () => {
    if (!currentThread || !newTitle.trim() || !currentUser) {
      setEditingTitle(false);
      return;
    }

    try {
      setLoading(true);
      
      // Update directly in Firestore
      const threadRef = doc(db, 'users', currentUser.uid, 'threads', currentThread.id);
      await setDoc(threadRef, {
        title: newTitle.trim(),
        lastMessageAt: serverTimestamp(),
      }, { merge: true });

      // Update local state
      setCurrentThread(prev => prev ? { ...prev, title: newTitle.trim() } : null);
      setEditingTitle(false);
      setError(null);
    } catch (err) {
      console.error('Error updating title:', err);
      setError('Failed to update chat title. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBookmark = async () => {
    if (!currentThread || !currentUser) return;

    try {
      setLoading(true);
      const threadRef = doc(db, 'users', currentUser.uid, 'threads', currentThread.id);
      
      const newBookmarkStatus = !currentThread.isBookmarked;
      await setDoc(threadRef, {
        isBookmarked: newBookmarkStatus,
        lastMessageAt: serverTimestamp(),
      }, { merge: true });

      // Update local state
      setCurrentThread(prev => prev ? { ...prev, isBookmarked: newBookmarkStatus } : null);
      setError(null);
    } catch (err) {
      console.error('Error toggling bookmark:', err);
      setError('Failed to update bookmark status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate remaining messages for free tier
  const getRemainingMessages = () => {
    if (tier === SubscriptionTier.FREE) {
      return Math.max(0, limits.dailyMessageLimit - dailyMessageCount);
    }
    return null; // Premium users don't have a limit
  };

  const remainingMessages = getRemainingMessages();

  if (error) {
    return (
      <Container>
        <Typography color="error" align="center" sx={{ mt: 4 }}>
          {error}
        </Typography>
        {quotaExceeded && (
          <Box sx={{ mt: 2 }}>
            <SubscriptionBanner 
              message="You've reached your daily message limit. Upgrade to premium for unlimited messages."
              showUpgrade={true}
            />
          </Box>
        )}
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, height: '80vh', display: 'flex', flexDirection: 'column' }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Error message if any */}
      {error && (
        <Typography color="error" align="center" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {currentThread && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingTitle ? (
              <>
                <TextField
                  size="small"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Enter chat title..."
                  autoFocus
                  sx={{ minWidth: 200 }}
                />
                <IconButton onClick={handleTitleSave} disabled={loading} color="primary">
                  <CheckIcon />
                </IconButton>
                <IconButton onClick={() => setEditingTitle(false)} disabled={loading}>
                  <CloseIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Typography variant="h6">
                  {currentThread.title || 'Untitled Chat'}
                </Typography>
                <IconButton onClick={handleTitleEdit} disabled={loading}>
                  <EditIcon />
                </IconButton>
                <IconButton 
                  onClick={handleToggleBookmark} 
                  disabled={loading}
                  color={currentThread.isBookmarked ? "primary" : "default"}
                >
                  <BookmarkIcon />
                </IconButton>
              </>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {tier === SubscriptionTier.FREE && remainingMessages !== null && (
            <Chip 
              label={`${remainingMessages} message${remainingMessages !== 1 ? 's' : ''} left today`}
              color={remainingMessages > 5 ? 'primary' : 'warning'}
              size="small"
            />
          )}
          {tier === SubscriptionTier.PREMIUM && (
            <Chip 
              label="Premium"
              color="success"
              size="small"
            />
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={createNewThread}
            disabled={loading}
            color="primary"
          >
            New Chat
          </Button>
        </Box>
      </Box>
      
      {currentThread ? (
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            height: 'calc(100% - 60px)',
            display: 'flex', 
            flexDirection: 'column',
            bgcolor: 'background.default'
          }}
        >
          <Box sx={{ 
            p: 1, 
            mb: 2, 
            bgcolor: 'primary.light', 
            color: 'primary.contrastText',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Typography variant="body2">
              This chat is powered by OpenAI Assistant API v2
            </Typography>
          </Box>
          
          <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
            {messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    maxWidth: '70%',
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography>{msg.content}</Typography>
                </Paper>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Ask a question..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading || (tier === SubscriptionTier.FREE && remainingMessages === 0)}
              sx={{ bgcolor: 'background.paper' }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading || !message.trim() || (tier === SubscriptionTier.FREE && remainingMessages === 0)}
              sx={{ minWidth: 100 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Send'}
            </Button>
          </Box>
          
          {/* Modified quota exceeded warning in the chat interface - no button */}
          {currentThread && tier === SubscriptionTier.FREE && remainingMessages === 0 && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'warning.light', 
              borderRadius: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Typography variant="body2">
                You've reached your daily message limit. Please upgrade to premium for unlimited messages.
              </Typography>
            </Box>
          )}
        </Paper>
      ) : (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: 'calc(100% - 60px)',
          bgcolor: 'background.paper',
          borderRadius: 1,
          p: 3
        }}>
          <Typography variant="h6" color="textSecondary" align="center">
            Click "New Chat" to start a conversation with OpenAI Assistant
          </Typography>
        </Box>
      )}
    </Container>
  );
} 